import { useAIGeneratedCoverImage } from '@atlassian/ai-image-generation/use-ai-generated-cover-image';
import { useRemixImageGeneration } from '@atlassian/ai-image-generation/use-remix-image-generation';
import type {
	GenerateImageEventCallbacks,
	UseAIGeneratedCoverImageReturn,
} from '@atlassian/ai-image-generation/types';

import { createEditorMediaProvider } from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import { fg } from '@confluence/feature-gating';

type UseHeaderImageGenerationProps = {
	cloudId: string;
	contentId: string;
	eventCallbacks?: GenerateImageEventCallbacks;
	userId: string | null;
};

/**
 * Selects the header-image generation backend while preserving the cover picture lifecycle.
 *
 * Remix returns the copied media ID as `NOT_LOADING` so EditorPageCoverPicture can persist it and
 * complete either the manual acceptance flow or the AIFC auto-apply flow before marking it as set.
 */
export const useHeaderImageGeneration = ({
	cloudId,
	contentId,
	eventCallbacks,
	userId,
}: UseHeaderImageGenerationProps): UseAIGeneratedCoverImageReturn => {
	const legacyImageGeneration = useAIGeneratedCoverImage({
		contentId,
		eventCallbacks,
	});
	const remixImageGeneration = useRemixImageGeneration({
		cloudId,
		contentId,
		createEditorMediaProvider,
		eventCallbacks,
		successfulGenerationStatus: 'NOT_LOADING',
		touchpointSource: 'confluence-header-image-generation',
		userId,
	});

	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	if (fg('header_images_migrate_image_gen_to_remix')) {
		return {
			...remixImageGeneration,
			data: null,
		};
	}

	return legacyImageGeneration;
};
